// Project issues for the steering board.
//
// `GET /api/projects/:id/issues` returns every issue projected to
// `DashboardIssue` using the resolved label→stage mapping. The
// `setIssues` opener exists so Phase 3b can apply optimistic reorder
// without re-fetching.

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
  DashboardIssueSchema,
  type DashboardIssue,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

const IssuesResponseSchema = z.object({
  issues: z.array(DashboardIssueSchema),
});

export interface ProjectIssuesState {
  readonly issues: ReadonlyArray<DashboardIssue>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
  readonly setIssues: (next: ReadonlyArray<DashboardIssue>) => void;
}

export interface ProjectIssuesOptions {
  /**
   * Fetch only open issues (`?state=opened`). The Focus refine search sets
   * this so a mature backlog's closed tail isn't pulled on every load. The
   * dashboard board omits it to keep its Done/Shipped columns populated.
   */
  readonly openOnly?: boolean;
}

export const useProjectIssues = (
  projectId: number,
  opts: ProjectIssuesOptions = {},
): ProjectIssuesState => {
  const openOnly = opts.openOnly ?? false;
  const [issues, setIssues] = useState<ReadonlyArray<DashboardIssue>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tick, setTick] = useState<number>(0);
  const aborted = useRef(false);

  useEffect(() => {
    aborted.current = false;
    setLoading(true);
    setError(null);
    httpGet(
      `/api/projects/${projectId}/issues${openOnly ? "?state=opened" : ""}`,
      IssuesResponseSchema,
    )
      .then((value) => {
        if (aborted.current) return;
        setIssues(value.issues);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [projectId, tick, openOnly]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  return { issues, loading, error, refetch, setIssues };
};
